<template>
  <el-tooltip content="Manage shortlists" placement="top">
    <el-button
      class="w-32"
      :type="isShortlisted ? 'success' : 'default'"
      size="small"
      @click="openShortlistsDialog()"
    >
      <span v-if="isShortlisted">
        Shortlisted
      </span>
      <span v-else>Add to shortlist</span>
      <Modal v-model="isOpen" :title="title" wide>
        <div v-loading="saveInProgress" v-if="isOpen">
          <div class="grid grid-cols-2 gap-4">
            <ShortlistList
              :startup="startup"
              :shortlists="shortlists.filter((s) => !s.organization_id)"
              @create="onShortlistCreate"
              @change-selected="onPrivateShortlistsSelectedChange"
            >
              <template v-slot:header>
                <h3 class="text-gray-300 text-base">
                  Private shortlists
                </h3>
                <p class="text-gray-200 text-xs">
                  Those are your private shortlists
                </p>
              </template>
            </ShortlistList>
            <ShortlistList
              :startup="startup"
              :shortlists="shortlists.filter((s) => s.organization_id)"
              :organization-id="org.id"
              @create="onShortlistCreate"
              @change-selected="onOrganizationShortlistsSelectedChange"
            >
              <template v-slot:header>
                <h3 class="text-gray-300 text-base">
                  Organization shortlists
                </h3>
                <p class="text-gray-200 text-xs">
                  Those shortlists are shared with your organization ({{
                    org.name
                  }})
                </p>
              </template>
            </ShortlistList>
          </div>
          <div>
            <Button type="primary" @click="save">
              Update shortlists
            </Button>
            <Button type="default" @click="closeModal">
              Close
            </Button>
          </div>
          <div v-if="DEBUG" class="my-4">
            <h4>Shortlists:</h4>
            <pre>{{ shortlists }}</pre>
            <h4>Org:</h4>
            <pre>{{ org }}</pre>
            <h4>Selected:</h4>
            <pre>{{ selectedShortlistIds }}</pre>
          </div>
        </div>
      </Modal>
    </el-button>
  </el-tooltip>
</template>

<script>
import Modal from 'components/common/Modal'
import Button from 'components/common/Button'
import TextButton from 'components/common/TextButton'
import Checkbox from 'components/forms/Checkbox'
import useState from 'states/generics/useState'
import { ref, inject, watch, computed } from '@vue/composition-api'
import { ShortistContextSymbol } from '@/contexts/ShortlistContext'
import ShortlistForm from './ShortlistForm.vue'
import ShortlistList from './ShortlistList.vue'
import $message from 'services/messages'
import $store from 'store'
import { useShortlists } from '@/states/shortlists/useShortlists'

const DEBUG = false

export default {
  components: {
    Button,
    TextButton,
    Checkbox,
    Modal,
    ShortlistForm,
    ShortlistList,
  },
  props: {
    startup: Object,
  },
  emits: ['create', 'update'],
  setup(props, { emit }) {
    const {
      shortlists,
      setStartupShortlists,
      isSetStartupShortlistsInProgress,
      org,
    } = inject(ShortistContextSymbol)

    const [isOpen, setIsOpen] = useState(false)
    const openShortlistsDialog = async () => {
      setIsOpen(true)
    }
    const closeModal = () => {
      setIsOpen(false)
    }

    const onShortlistCreate = (shortlist) => {
      emit('create', shortlist)
      save({ msg: 'Startup added to new shortlist' })
    }

    const privateSelected = ref([])
    const orgSelected = ref([])

    const onPrivateShortlistsSelectedChange = (selected) => {
      privateSelected.value = selected
    }
    const onOrganizationShortlistsSelectedChange = (selected) => {
      orgSelected.value = selected
    }
    const selectedShortlistIds = computed(() => {
      const newIds = [...privateSelected.value, ...orgSelected.value]
      return newIds
    })
    const selectedShortlists = computed(() => {
      const shortlistsMap = {}
      shortlists.value.forEach((s) => {
        shortlistsMap[s.id] = s
      })
      return selectedShortlistIds.value.map((id) => shortlistsMap[id])
    })

    const isShortlisted = computed(() => {
      return props.startup && (props.startup.shortlists || []).length
    })
    const title = `Manage shortlists for ${props.startup.name}`

    const save = async ({ msg } = {}) => {
      await setStartupShortlists({
        startupId: props.startup.id,
        shortlistIds: selectedShortlistIds.value,
      })
      const shortlists = selectedShortlists.value
      const attrs = { shortlists }
      $store.commit('startupList/setStartupAttrs', {
        startupId: props.startup.id,
        attrs,
      })
      $store.commit('startupProfile/SET_STARTUP_ATTR', {
        attr: 'shortlists',
        value: shortlists,
      })
      if (msg === undefined) {
        msg = 'Shortlists updated'
      }
      $message({ type: 'success', message: msg })
      emit('update', { shortlists })
      closeModal()
    }

    return {
      shortlists,

      isShortlisted,
      title,

      isOpen,
      openShortlistsDialog,
      closeModal,

      onShortlistCreate,

      save,
      saveInProgress: isSetStartupShortlistsInProgress,

      org,
      DEBUG,
      selectedShortlistIds,

      onPrivateShortlistsSelectedChange,
      onOrganizationShortlistsSelectedChange,
    }
  },
}
</script>
